import { z } from "zod";

/**
 * Validación cliente de admins. El back valida password con más reglas
 * (longitud, complejidad, breach check) — nosotros validamos el mínimo
 * útil para feedback inmediato en el form.
 */
const passwordSchema = z
  .string({ required_error: "Define una contraseña" })
  .min(12, "Mínimo 12 caracteres")
  .max(128, "Máximo 128 caracteres")
  .refine((v) => /[a-z]/.test(v), "Debe tener minúsculas")
  .refine((v) => /[A-Z]/.test(v), "Debe tener mayúsculas")
  .refine((v) => /[0-9]/.test(v), "Debe tener un número");

const commonFields = {
  email: z.string({ required_error: "El email es obligatorio" }).trim().email("Email inválido"),
  full_name: z
    .string({ required_error: "El nombre es obligatorio" })
    .trim()
    .min(2, "Mínimo 2 caracteres")
    .max(80, "Máximo 80 caracteres"),
  role: z.string({ required_error: "Elige un rol" }).min(1, "Elige un rol"),
};

/**
 * Create de admin — dos modos (discriminated union):
 *
 *   - `new`: cuenta de staff totalmente nueva. Pide `password`.
 *   - `link`: vinculación con una cuenta usuaria ya existente en el app
 *     móvil (mismo correo). El password lo maneja la usuaria desde el
 *     app; acá no se envía.
 *
 * Al serializar al body del back:
 *   - `new`  → `{ email, full_name, role, password }`
 *   - `link` → `{ email, full_name, role, link_existing_account: true }`
 */
export const AdminUserCreateSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("new"),
    ...commonFields,
    password: passwordSchema,
  }),
  z.object({
    mode: z.literal("link"),
    ...commonFields,
  }),
]);
export type AdminUserCreate = z.infer<typeof AdminUserCreateSchema>;

/** Edit: full_name + role + active. Email y password se gestionan aparte. */
export const AdminUserEditSchema = z.object({
  full_name: z.string().trim().min(2, "Mínimo 2 caracteres").max(80, "Máximo 80 caracteres"),
  role: z.string().min(1, "Elige un rol"),
  active: z.boolean(),
});
export type AdminUserEdit = z.infer<typeof AdminUserEditSchema>;

/** Para calcular fuerza visual del password al tipearlo. */
export function passwordStrength(password: string): {
  score: 0 | 1 | 2 | 3 | 4;
  label: "muy débil" | "débil" | "regular" | "fuerte" | "excelente";
} {
  if (!password) return { score: 0, label: "muy débil" };
  let score = 0;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) score++;
  const labels = ["muy débil", "débil", "regular", "fuerte", "excelente"] as const;
  const bounded = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
  return { score: bounded, label: labels[bounded] };
}

/**
 * Lee del `ErrorResponse` del back si el error de conflicto permite
 * vincular una cuenta ya existente (campo `details.can_link`).
 *
 * Return:
 *   - `true` si el back dice que podemos reintentar con
 *     `link_existing_account: true`.
 *   - `false` en cualquier otro caso.
 */
export function canLinkFromError(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("data" in error)) return false;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object" || !("details" in data)) return false;
  const details = (data as { details?: unknown }).details;
  if (!details || typeof details !== "object") return false;
  return (details as Record<string, unknown>).can_link === true;
}
